import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo } from 'react';

import { EventLogPaginatedFeedParamsGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { useEventLogFilters } from '../EventLogFilters';
import { useGetEventLogsQuery } from '../queries/GetEventLogs.gql.generated';

// Constants
const PAGINATION_LIMIT = 20;

// Exports
/**
 * Clear event logs cache on mount. The usual `fetchPolicy: "network-option"` does not work
 * because cursor-based pagination relies on us manually adding and removing items from the
 * cache using Apollo type policies
 */
export const useEventLogs: typeof useGetEventLogsQuery = (options) => {
  const client = useApolloClient();
  useEffect(() => {
    client.cache.evict({ fieldName: 'eventLogs' });
    client.cache.gc();
  }, [client]);

  return useGetEventLogsQuery(options);
};

export const useEventLogQueryFilteredParams = () => {
  const actorIds = useEventLogFilters((s) => s.actorIds);
  const creditFacilityIds = useEventLogFilters((s) => s.creditFacilityIds);
  const dateRange = useEventLogFilters((s) => s.dateRange);
  const deliverableIds = useEventLogFilters((s) => s.deliverableIds);
  const fundingRequestIds = useEventLogFilters((s) => s.fundingRequestIds);
  const invoiceIds = useEventLogFilters((s) => s.invoiceIds);
  const limitedPartnerIds = useEventLogFilters((s) => s.limitedPartnerIds);
  const selectedEventTypes = useEventLogFilters((s) => s.selectedEventTypes);
  const valleyBankLoanAccountIds = useEventLogFilters(
    (s) => s.valleyBankLoanAccountIds,
  );

  const commonParams = useMemo<EventLogPaginatedFeedParamsGQLType>(() => {
    const entityIds = [
      ...deliverableIds,
      ...fundingRequestIds,
      ...invoiceIds,
      ...limitedPartnerIds,
      ...valleyBankLoanAccountIds,
    ];

    return {
      version: 2,
      eventLogTypes: selectedEventTypes,
      entityIds: entityIds.length ? entityIds : undefined,
      actorIds: actorIds.length ? actorIds : undefined,
      creditFacilityIds: creditFacilityIds.length
        ? creditFacilityIds
        : undefined,
      startDate: dateRange.startDate
        ? dateRange.startDate.getTime()
        : undefined,
      endDate: dateRange.endDate ? dateRange.endDate.getTime() : undefined,
      limit: PAGINATION_LIMIT,
      sortBy: 'created_at' as const,
      sortDirection: 'desc' as const,
    };
  }, [
    actorIds,
    creditFacilityIds,
    dateRange,
    deliverableIds,
    fundingRequestIds,
    invoiceIds,
    limitedPartnerIds,
    selectedEventTypes,
    valleyBankLoanAccountIds,
  ]);

  return commonParams;
};
